import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

import { getDesktopLibmpvBridge, type DesktopLibmpvEvent } from '@maxus/website/lib/desktop'

import type { PlayerBackendProps, PlayerHandle } from '../types'

let nextPlayerId = 0

const LibmpvPlayer = forwardRef<PlayerHandle, PlayerBackendProps>(({ source, className, ...callbacks }, ref) => {
  const playerIdRef = useRef(`libmpv-${++nextPlayerId}`)
  const surfaceRef = useRef<HTMLDivElement>(null)
  const bridgeRef = useRef(getDesktopLibmpvBridge())
  const callbacksRef = useRef(callbacks)
  const sourceRef = useRef(source)
  const createdRef = useRef(false)
  const loadedUrlRef = useRef<string | null>(null)
  const [hasVideo, setHasVideo] = useState(false)
  callbacksRef.current = callbacks
  sourceRef.current = source

  const handleEvent = (event: DesktopLibmpvEvent) => {
    const current = callbacksRef.current
    switch (event.type) {
      case 'play':
        current.onPlay?.()
        break
      case 'playing':
        setHasVideo(true)
        current.onPlaying?.()
        break
      case 'pause':
        current.onPause?.()
        break
      case 'waiting':
        current.onWaiting?.()
        break
      case 'ended':
        current.onEnded?.()
        break
      case 'time-update':
        current.onTimeUpdate?.(event.currentTime)
        break
      case 'duration-change':
        current.onDurationChange?.(event.duration)
        break
      case 'volume-change':
        current.onVolumeChange?.(event.volume, event.muted)
        break
      case 'loaded-metadata':
        setHasVideo(true)
        current.onLoadedMetadata?.({ width: event.width, height: event.height })
        break
      case 'error':
        current.onError?.(new Error(event.message))
        break
    }
  }

  useImperativeHandle(
    ref,
    () => ({
      play: () => bridgeRef.current?.play(playerIdRef.current),
      pause: () => bridgeRef.current?.pause(playerIdRef.current),
      seek: time => bridgeRef.current?.seek(playerIdRef.current, time),
      setVolume: volume => bridgeRef.current?.setVolume(playerIdRef.current, volume),
      setMuted: muted => bridgeRef.current?.setMuted(playerIdRef.current, muted),
      setPlaybackRate: rate => bridgeRef.current?.setPlaybackRate(playerIdRef.current, rate),
    }),
    [],
  )

  useLayoutEffect(
    () => {
      const bridge = bridgeRef.current
      const surface = surfaceRef.current
      if (!bridge || !surface) {
        callbacksRef.current.onError?.(new Error('The Electron libmpv bridge is unavailable'))
        return
      }

      const playerId = playerIdRef.current
      let disposed = false
      const updateBounds = () => {
        const rect = surface.getBoundingClientRect()
        bridge.setBounds(playerId, {
          x: rect.x,
          y: rect.y,
          width: rect.width,
          height: rect.height,
        })
      }
      const observer = new ResizeObserver(updateBounds)
      observer.observe(surface)
      window.addEventListener('resize', updateBounds)
      window.addEventListener('scroll', updateBounds, true)
      document.addEventListener('fullscreenchange', updateBounds)
      const unsubscribe = bridge.onEvent((eventPlayerId, event) => {
        if (eventPlayerId === playerId) handleEvent(event)
      })

      const initialUrl = sourceRef.current.src
      const rect = surface.getBoundingClientRect()
      void bridge
        .create(
          playerId,
          {
            x: rect.x,
            y: rect.y,
            width: rect.width,
            height: rect.height,
          },
          initialUrl,
        )
        .then(() => {
          if (disposed) {
            bridge.destroy(playerId)
            return
          }
          createdRef.current = true
          loadedUrlRef.current = initialUrl
          const latestUrl = sourceRef.current.src
          if (latestUrl !== initialUrl) {
            loadedUrlRef.current = latestUrl
            void bridge
              .load(playerId, latestUrl)
              .catch(error => callbacksRef.current.onError?.(error))
          }
        })
        .catch(error => callbacksRef.current.onError?.(error))

      return () => {
        disposed = true
        createdRef.current = false
        loadedUrlRef.current = null
        observer.disconnect()
        window.removeEventListener('resize', updateBounds)
        window.removeEventListener('scroll', updateBounds, true)
        document.removeEventListener('fullscreenchange', updateBounds)
        unsubscribe()
        bridge.destroy(playerId)
      }
    },
    [],
  )

  useEffect(
    () => {
      const bridge = bridgeRef.current
      if (!bridge || !createdRef.current || loadedUrlRef.current === source.src) return
      setHasVideo(false)
      loadedUrlRef.current = source.src
      void bridge
        .load(playerIdRef.current, source.src)
        .catch(error => callbacksRef.current.onError?.(error))
    },
    [source.src],
  )

  return (
    <div ref={surfaceRef} className={className} data-libmpv-active={hasVideo ? '' : undefined}>
      {!hasVideo && source.poster ? <img alt="" className="h-full w-full object-contain" src={source.poster} /> : null}
    </div>
  )
})

LibmpvPlayer.displayName = 'LibmpvPlayer'

export { LibmpvPlayer }
